import { cn } from "@volca/ui/lib/utils";
import { EDITOR_PALETTE } from "@/core/colors";

/** How many squares the proportion bar is drawn from. */
const SQUARES = 5;

const SQUARE = "h-2 w-2 rounded-[2px]";

/**
 * How many of the five squares are green, the rest being red.
 *
 * A side with any lines at all keeps a square, however small its share. The
 * alternative rounds the one line removed in a long rewrite down to nothing,
 * and a bar that says "only additions" beside a count that says otherwise is
 * worse than no bar.
 *
 * All five are always coloured. GitHub leaves some blank to say how big one
 * file is against the whole change, and there is no second document here to
 * compare against, so a blank square would mean nothing.
 */
const greenSquares = (added: number, removed: number) => {
  if (!removed) return SQUARES;
  if (!added) return 0;

  return Math.min(
    Math.max(1, Math.round((added / (added + removed)) * SQUARES)),
    SQUARES - 1,
  );
};

/**
 * How much of the written plan the proposal rewrites, as a count and a bar.
 *
 * The edit count beside it says how many separate places changed, which is what
 * you step through. This says how much text moved, which is a different
 * question: one edit can be a word or a whole section.
 *
 * The numbers carry the meaning and the bar only repeats it, so the colour is
 * never the only signal.
 */
export const DiffTotals = ({
  added,
  removed,
  className,
}: {
  added: number;
  removed: number;
  className?: string;
}) => {
  if (!added && !removed) return null;

  const green = greenSquares(added, removed);
  return (
    <span className={cn("flex items-center gap-1.5", className)}>
      <span className="sr-only">
        {`${added} ${added === 1 ? "line" : "lines"} added, ${removed} ${removed === 1 ? "line" : "lines"} removed`}
      </span>
      <span
        aria-hidden
        className={cn("font-medium", EDITOR_PALETTE.added.foreground)}
      >
        +{added}
      </span>
      <span
        aria-hidden
        className={cn("font-medium", EDITOR_PALETTE.removed.foreground)}
      >
        &minus;{removed}
      </span>
      <span aria-hidden className="flex items-center gap-0.5">
        {Array.from({ length: SQUARES }, (_, index) => (
          <span
            key={index}
            className={cn(SQUARE, {
              [EDITOR_PALETTE.added.solid]: index < green,
              [EDITOR_PALETTE.removed.solid]: index >= green,
            })}
          />
        ))}
      </span>
    </span>
  );
};
